import { useCallback, useMemo, useRef, useState } from 'react';

export type UseSelectionValuesResult<T> = {
  clear: () => void;
  toggle: (ids: T | T[]) => void;
  set: (ids: T | T[], value: boolean) => void;
  selected: T[];
  count: number;
};

export default function useSelectionValues<T>(initialState?: T[]): UseSelectionValuesResult<T> {
  const { current: selectedItemSet } = useRef<Set<T>>(new Set(initialState));
  const [, setState] = useState(0);
  const rerender = () => setState((i) => i + 1);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selected = useMemo(() => Array.from(selectedItemSet), [selectedItemSet.size]);

  const clear = useCallback(() => {
    selectedItemSet.clear();

    rerender();
  }, [selectedItemSet]);

  const set = useCallback(
    (ids: T | T[], selected: boolean) => {
      if (!Array.isArray(ids)) {
        // eslint-disable-next-line no-param-reassign
        ids = [ids];
      }
      ids.forEach((id) => {
        if (selected) {
          selectedItemSet.add(id);
        } else {
          selectedItemSet.delete(id);
        }
      });

      rerender();
    },
    [selectedItemSet]
  );

  const toggle = useCallback(
    (ids: T | T[]) => {
      if (!Array.isArray(ids)) {
        // eslint-disable-next-line no-param-reassign
        ids = [ids];
      }

      ids.forEach((id) => {
        if (selectedItemSet.has(id)) {
          selectedItemSet.delete(id);
        } else {
          selectedItemSet.add(id);
        }
      });

      rerender();
    },
    [selectedItemSet]
  );

  return {
    count: selectedItemSet.size,
    selected,
    clear,
    set,
    toggle,
  };
}
